import React from "react";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";

import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Skeleton,
  IconButton as MuiIconButton,
} from "@mui/material";
import {
  DoDisturb as UnVerifiedIcon,
  CheckCircleOutline as VerifiedIcon,
  GroupOutlined as GroupOutlinedIcon,
} from "@mui/icons-material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";

import useUser from "../../../../hooks/super/useUser";
import { formatDate, getUserRoleString } from "../../../../utils/format";
import { TextField } from "../../../../components/pages/common/styled";
import { getComparator, stableSort } from "../../../../utils/tableSort";
import TablePagination from "../../../../components/common/TablePagination";

import {
  TOKENS,
  Monogram,
  StatusPill,
  EmptyState,
} from "../../../../components/pages/_new/super/design";

const headCells = [
  { id: "firstName", label: "User", sortable: true },
  { id: "org", label: "Organisation", sortable: true },
  { id: "role", label: "Role", sortable: true },
  { id: "title", label: "Title", sortable: true },
  { id: "created", label: "Created", sortable: true },
  { id: "last_login", label: "Last login", sortable: true },
  { id: "isVerified", label: "Verified", sortable: true },
  { id: "enabled", label: "Status", sortable: true },
];

const HeaderCell = ({ children, sortable, id, orderBy, order, onSort, align }) => (
  <TableCell
    align={align || "left"}
    sx={{
      padding: "10px 14px",
      borderBottom: `1px solid ${TOKENS.border}`,
      background: TOKENS.bgSunken,
      color: TOKENS.fg2,
      fontSize: 11.5,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
    }}
  >
    {sortable ? (
      <TableSortLabel
        active={orderBy === id}
        direction={orderBy === id ? order : "asc"}
        onClick={() => onSort(id)}
        sx={{
          color: `${TOKENS.fg2} !important`,
          "& .MuiTableSortLabel-icon": { color: `${TOKENS.fg2} !important` },
        }}
      >
        {children}
      </TableSortLabel>
    ) : (
      children
    )}
  </TableCell>
);

const DataCell = ({ children, align }) => (
  <TableCell
    align={align || "left"}
    sx={{
      padding: "12px 14px",
      borderBottom: `1px solid ${TOKENS.borderSubtle}`,
      color: TOKENS.fg1,
      fontSize: 13.5,
    }}
  >
    {children}
  </TableCell>
);

const IdMono = ({ value }) => (
  <Typography
    sx={{
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontSize: 11,
      color: TOKENS.fg3,
    }}
  >
    {value}
  </Typography>
);

const VerifiedDot = ({ value }) =>
  value ? (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <VerifiedIcon sx={{ fontSize: 16, color: TOKENS.successInk }} />
      <Typography sx={{ fontSize: 13, color: TOKENS.successInk, fontWeight: 500 }}>
        Verified
      </Typography>
    </Stack>
  ) : (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <UnVerifiedIcon sx={{ fontSize: 16, color: TOKENS.fg3 }} />
      <Typography sx={{ fontSize: 13, color: TOKENS.fg3 }}>Unverified</Typography>
    </Stack>
  );

const OrgCell = ({ org }) => {
  const title = org?.title || "—";
  if (!org?.title) {
    return <Typography sx={{ fontSize: 13, color: TOKENS.fg3 }}>—</Typography>;
  }
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Monogram name={title} size={28} />
      <Typography sx={{ fontSize: 13, color: TOKENS.fg1, fontWeight: 500 }}>
        {title}
      </Typography>
    </Stack>
  );
};

const DateCell = ({ value }) => {
  if (!value) {
    return <Typography sx={{ fontSize: 13, color: TOKENS.fg3 }}>—</Typography>;
  }
  return (
    <Typography sx={{ fontSize: 13, color: TOKENS.fg1 }}>
      {formatDate(value)}
    </Typography>
  );
};

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;
  const [_page, set_page] = React.useState(page);
  React.useEffect(() => {
    set_page(page + 1);
  }, [page]);

  const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);

  const handleFirstPageButtonClick = (event) => onPageChange(event, 0);
  const handleBackButtonClick = (event) => onPageChange(event, page - 1);
  const handleNextButtonClick = (event) => onPageChange(event, page + 1);
  const handleLastPageButtonClick = (event) => onPageChange(event, lastPage);

  const handleCustomTextChange = (event) => {
    if (event.key === "Enter") {
      if (event.target.value > lastPage) {
        set_page(lastPage + 1);
        onPageChange(event, lastPage);
      } else if (event.target.value < 1) {
        set_page(1);
        onPageChange(event, 0);
      } else {
        set_page(event.target.value);
        onPageChange(event, event.target.value - 1);
      }
    }
  };
  const handlePageChange = (event) => set_page(event.target.value);

  const navBtnSx = {
    p: "6px",
    borderRadius: "8px",
    color: TOKENS.fg2,
    "&:hover": { background: TOKENS.bgSunken, color: TOKENS.fg1 },
    "&.Mui-disabled": { color: TOKENS.fg3 },
  };

  return (
    <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", ml: 4 }}>
      <MuiIconButton sx={navBtnSx} onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </MuiIconButton>
      <MuiIconButton sx={navBtnSx} onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </MuiIconButton>
      <TextField
        onKeyDown={handleCustomTextChange}
        onChange={handlePageChange}
        type="number"
        variant="standard"
        value={_page}
        InputProps={{ inputProps: { style: { textAlign: "center" } } }}
      />
      <MuiIconButton sx={navBtnSx} onClick={handleNextButtonClick} disabled={page >= lastPage} aria-label="next page">
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </MuiIconButton>
      <MuiIconButton sx={navBtnSx} onClick={handleLastPageButtonClick} disabled={page >= lastPage} aria-label="last page">
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </MuiIconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

function EnhancedTable() {
  const {
    activeUsers,
    activeTotal,
    time_range,
    activeFrom,
    activeSize,
    getActiveUserReport,
  } = useUser();
  const totalCount = activeTotal;
  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("last_login");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  React.useEffect(() => {
    setPage(activeFrom / activeSize);
  }, [activeFrom]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    setRowsPerPage(activeSize);
  }, [activeSize]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRequestSort = (id) => {
    if (id === orderBy) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(id);
      setOrder("asc");
    }
  };

  const handleChangePage = async (event, activePage) => {
    setPage(activePage);
    getActiveUserReport(time_range, rowsPerPage, rowsPerPage * activePage);
  };

  const handleChangeRowsPerPage = async (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
    getActiveUserReport(time_range, parseInt(event.target.value, 10), 0);
  };

  const isLoading = activeUsers === null;
  const sorted = !isLoading
    ? stableSort(activeUsers, getComparator(order, orderBy))
    : [];

  return (
    <Box
      sx={{
        border: `1px solid ${TOKENS.border}`,
        borderRadius: `${TOKENS.radiusS}px`,
        background: TOKENS.bgSurface,
        boxShadow: TOKENS.shadowXs,
        overflow: "hidden",
      }}
    >
      <TableContainer>
        <Table aria-label="active users table" sx={{ minWidth: 980 }}>
          <TableHead>
            <TableRow>
              {headCells.map((c) => (
                <HeaderCell
                  key={c.id}
                  id={c.id}
                  sortable={c.sortable}
                  orderBy={orderBy}
                  order={order}
                  onSort={handleRequestSort}
                >
                  {c.label}
                </HeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from({ length: rowsPerPage || 5 }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  {headCells.map((__, j) => (
                    <DataCell key={`sk-${i}-${j}`}>
                      <Skeleton
                        variant="rectangular"
                        height={18}
                        sx={{ borderRadius: 1 }}
                      />
                    </DataCell>
                  ))}
                </TableRow>
              ))
            ) : sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={headCells.length} sx={{ borderBottom: "none" }}>
                  <EmptyState
                    icon={<GroupOutlinedIcon />}
                    title="No active users"
                    body="No active users in the selected time window."
                  />
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((row, index) => {
                const fullName =
                  `${row?.firstName || ""} ${row?.lastName || ""}`.trim() ||
                  row?.email ||
                  "—";
                const status = row?.isDeleted
                  ? "Suspended"
                  : row?.enabled
                  ? row?.isVerified
                    ? "Active"
                    : "Pending"
                  : "Suspended";
                return (
                  <TableRow
                    key={`${row?.id}-${index}`}
                    hover
                    sx={{ "&:hover": { background: TOKENS.bgSunken } }}
                  >
                    <DataCell>
                      <Stack direction="row" alignItems="center" spacing={1.25}>
                        <Monogram name={fullName} size={36} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 13.5,
                              fontWeight: 600,
                              color: TOKENS.fg1,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {fullName}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: TOKENS.fg2 }}>
                            {row?.email || "—"}
                          </Typography>
                          {row?.id != null && <IdMono value={row.id} />}
                        </Box>
                      </Stack>
                    </DataCell>
                    <DataCell>
                      <OrgCell org={row?.organisation} />
                    </DataCell>
                    <DataCell>
                      <Typography sx={{ fontSize: 13, color: TOKENS.fg1 }}>
                        {getUserRoleString(row?.role) || "—"}
                      </Typography>
                    </DataCell>
                    <DataCell>
                      <Typography sx={{ fontSize: 13, color: TOKENS.fg2 }}>
                        {row?.title || "—"}
                      </Typography>
                    </DataCell>
                    <DataCell>
                      <DateCell value={row?.created} />
                    </DataCell>
                    <DataCell>
                      <DateCell value={row?.last_login} />
                    </DataCell>
                    <DataCell>
                      <VerifiedDot value={row?.isVerified} />
                    </DataCell>
                    <DataCell>
                      <StatusPill status={status} />
                    </DataCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        SelectProps={{
          inputProps: { "aria-label": "rows per page" },
          native: true,
        }}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        ActionsComponent={TablePaginationActions}
      />
    </Box>
  );
}

function ActiveUserList() {
  return <EnhancedTable />;
}

export default ActiveUserList;
